import { useState, useRef, useCallback } from 'react';

import { useAtomValue } from 'jotai';

import type { Message } from '../../contexts/AsideContext';
import { DEFAULT_LABS_CLOUD_ID } from '../../pages/labs/home/constants';
import { JSON_CONTENT_TYPE } from '../../state/graphqlEndpoints';
import { SelectedSiteCloudIdAtom, CypherQueryAtom, type CyphieMode } from '../../state/LabsAtoms';

type AssistantMessage = {
	role: 'user' | 'assistant';
	content: Array<{
		type: 'text';
		text: string;
	}>;
};

type NL2CypherPayload = {
	model: {
		family: 'claude' | 'gpt';
		version: string;
	};
	messages: AssistantMessage[];
	params?: {
		maxTokens?: number;
		temperature?: number;
	};
	system?: string;
	thinking?: 'none' | 'summary';
};

type LineExplanation = {
	line: number;
	code: string;
	explanation: string;
};

type CypherExplanation = {
	formattedQuery: string;
	lineByLineExplanations: LineExplanation[];
};

type NL2CypherResponse = {
	summary?: string;
	cypherQuery?: string;
	cypherExplanation?: CypherExplanation;
	data?: unknown[];
	usage?: {
		input_tokens: number;
		output_tokens: number;
		total_tokens: number;
	};
	error?: {
		message: string;
		type: string;
	};
	errors?: Array<{
		message: string;
		type: string;
	}>;
};

export type NL2CypherResult = {
	content: string;
	rawCypherQuery?: string;
};

/**
 * Converts chat messages to the format expected by the NL2Cypher API
 */
function messagesToApiFormat(messages: Message[]): AssistantMessage[] {
	return messages
		.filter((msg) => msg.id !== 'welcome-message') // Exclude welcome message
		.map((msg) => ({
			role: msg.role,
			content: [{ type: 'text' as const, text: msg.content }],
		}));
}

/**
 * Builds a formatted Cypher query with explanatory comments above each line
 */
function buildAnnotatedQuery(cypherExplanation: CypherExplanation): string {
	const lines: string[] = [];

	for (const lineExplanation of cypherExplanation.lineByLineExplanations) {
		// Add the explanation as a comment above the code line
		lines.push(`// ${lineExplanation.explanation}`);
		lines.push(lineExplanation.code);
	}

	return lines.join('\n');
}

/**
 * Formats the response from NL2Cypher into a readable assistant message
 */
function formatResponse(response: NL2CypherResponse): NL2CypherResult {
	// Handle errors
	if (response.error) {
		return {
			content: `I encountered an error: ${response.error.message}`,
		};
	}

	if (response.errors && response.errors.length > 0) {
		return {
			content: `I couldn't process that request: ${response.errors[0].message}`,
		};
	}

	const parts: string[] = [];

	// Add summary if available
	if (response.summary) {
		parts.push(response.summary);
	}

	// Build the Cypher query with annotations if cypherExplanation is available
	let displayQuery: string | undefined;
	let rawQuery: string | undefined;

	if (response.cypherExplanation) {
		displayQuery = buildAnnotatedQuery(response.cypherExplanation);
		rawQuery = response.cypherExplanation.formattedQuery;
	} else if (response.cypherQuery) {
		displayQuery = response.cypherQuery;
		rawQuery = response.cypherQuery;
	}

	// Add generated Cypher query if available
	if (displayQuery) {
		parts.push('\n```cypher\n' + displayQuery + '\n```');
	} else {
		parts.push("I processed your request but didn't get a Cypher query back.");
	}

	return {
		content: parts.join('\n') || "I processed your request but didn't get a specific response.",
		rawCypherQuery: rawQuery,
	};
}

type UseNL2CypherOptions = {
	external?: boolean;
};

const MODEL_CONFIGS: Record<CyphieMode, NL2CypherPayload['model']> = {
	default: { family: 'gpt', version: '4.1-2025-04-14' },
	reasoning: { family: 'claude', version: 'latest' },
};

export function useNL2Cypher(options?: UseNL2CypherOptions): {
	sendMessage: (
		userMessage: string,
		conversationHistory: Message[],
		mode?: CyphieMode,
	) => Promise<NL2CypherResult>;
	cancel: () => void;
	isLoading: boolean;
	error: string | null;
} {
	const { external = false } = options ?? {};
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const abortControllerRef = useRef<AbortController | null>(null);

	// Get cloudId from state, fallback to default
	const selectedCloudId = useAtomValue(SelectedSiteCloudIdAtom);
	const cloudId = selectedCloudId || DEFAULT_LABS_CLOUD_ID;

	// Get current Cypher query for context
	const currentCypherQuery = useAtomValue(CypherQueryAtom);

	const sendMessage = useCallback(
		async (
			userMessage: string,
			conversationHistory: Message[],
			mode: CyphieMode = 'default',
		): Promise<NL2CypherResult> => {
			// Abort any existing request
			abortControllerRef.current?.abort('Starting new query');

			const controller = new AbortController();
			abortControllerRef.current = controller;

			setIsLoading(true);
			setError(null);

			try {
				// Build the system prompt
				const systemPrompt = `You are Cyphie, a helpful AI assistant for Teamwork Graph. You help users write performant Cypher queries. Be concise but informative. 
                    Use user:IdentityUser {ari: $me } in place of a full text ari if the ari in question is the user's ari. 
                    We prefer variables over inline aris. 
                    We prefer unions over option matches, where appropriate. 
                    We like to use (datetime() - duration('P7D')) over explicit datetime timestamps`;

				// Prepare messages for the API
				const apiMessages = messagesToApiFormat(conversationHistory);

				// Build the user message text, including the current editor query if available
				let messageText = userMessage;
				if (currentCypherQuery?.trim()) {
					messageText = `${userMessage}\n\nCurrent query in the editor:\n\`\`\`cypher\n${currentCypherQuery}\n\`\`\``;
				}

				// Add the new user message
				apiMessages.push({
					role: 'user',
					content: [{ type: 'text', text: messageText }],
				});

				const modelConfig = MODEL_CONFIGS[mode];
				const payload: NL2CypherPayload = {
					model: modelConfig,
					messages: apiMessages,
					params: {
						maxTokens: 2000,
						temperature: 0.7,
					},
					system: systemPrompt,
					thinking: 'none',
				};

				const nl2cypherPath = external
					? `/gateway/api/twg-api-v2/${cloudId}/v2/nl2cypher/external/chat/completions`
					: `/gateway/api/twg-api-v2/${cloudId}/v2/nl2cypher/chat/completions`;

				const requestHeaders: Record<string, string> = {
					Accept: JSON_CONTENT_TYPE,
					'Content-Type': JSON_CONTENT_TYPE,
					'X-Query-Context': `ari:cloud:rovo::site/${cloudId}`,
					'x-cypher-only': 'true',
				};

				if (modelConfig.family === 'gpt') {
					requestHeaders['x-generate-cypher-by-anatomy'] = 'true';
				}

				const response = await fetch(nl2cypherPath, {
					method: 'POST',
					credentials: 'include',
					headers: requestHeaders,
					body: JSON.stringify(payload),
					signal: controller.signal,
				});

				const data: NL2CypherResponse = await response.json();

				// Handle non-OK responses (except 400 which may contain useful error info)
				if (!response.ok && response.status !== 400) {
					throw new Error(`Request failed: ${response.status}`);
				}

				return formatResponse(data);
			} catch (err) {
				if (controller.signal.aborted) {
					return { content: 'Request was cancelled.' };
				}

				const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
				setError(errorMessage);
				return { content: `Sorry, I encountered an error: ${errorMessage}. Please try again.` };
			} finally {
				setIsLoading(false);
			}
		},
		[cloudId, currentCypherQuery, external],
	);

	const cancel = useCallback((): void => {
		abortControllerRef.current?.abort('User cancelled');
	}, []);

	return {
		sendMessage,
		cancel,
		isLoading,
		error,
	};
}
